import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { dsoApi } from '../../services/api';
import { 
  FaArrowLeft, 
  FaCaretSquareDown, 
  FaCheckCircle, 
  FaCross, 
  FaExclamationTriangle,
  FaFly,
  FaFrown,
  FaPlane,
  FaPrint,
  FaShip,
  FaStopCircle
} from 'react-icons/fa';

function InvoiceDetails() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Add these state variables below the existing ones
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  
  // Reference for the printable invoice section
  const printableRef = useRef(null);

  // Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString; // Fallback
  }
};

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Fetch invoice details on component mount
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      setLoading(true);
      setError('');
      
      try {
        const response = await dsoApi.getInvoiceDetails(invoiceId);
        setInvoice(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching invoice details:", err);
        setError('Failed to load invoice details.');
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [invoiceId]);

  // Function to handle printing the invoice
  const handlePrintInvoice = () => {
    const printContents = printableRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    const printStyles = `
      @media print {
        body { font-family: Arial, sans-serif; color: #333; }
        .invoice-container { padding: 20px; }
        .invoice-header { margin-bottom: 30px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
        .text-center { text-align: center; }
        .text-end { text-align: right; }
        .invoice-summary { margin-top: 20px; margin-left: auto; width: 300px; }
        .invoice-status { display: none; }
        .action-buttons-container { display: none; }
      }
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Invoice - ${invoice?.invoice_no || 'Invoice'}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          <div class="print-container">${printContents}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Add this function inside the InvoiceDetails component
  const handleUpdateStatus = async (approvalStatus, paymentStatus) => {
    setActionLoading(true);
    setActionError('');
    setActionSuccess('');
    try {
      // Call the API function we defined earlier
      const response = await dsoApi.updateInvoiceStatus(invoiceId, {
        approval_status: approvalStatus,
        payment_status: paymentStatus,
      });
      
      // Update local invoice state with the response data from the API
      // The backend returns the full updated invoice detail structure
      setInvoice(response.data); 
      setActionSuccess('Invoice status updated successfully.');
      
    } catch (err) {
      console.error("Error updating invoice status:", err);
      const errorMsg = err.response?.data?.detail || 'Failed to update invoice status.';
      setActionError(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  // Function to render status badge
  const renderStatusBadge = (status, type = 'approval') => {
    let variant = '';
    switch (status?.toLowerCase()) {
      case 'approved':
        variant = 'success';
        break;
      case 'rejected':
        variant = 'danger';
        break;
      case 'pending':
        variant = 'warning';
        break;
      case 'completed':
        variant = 'success';
        break;
      case 'failed':
        variant = 'danger';
        break;
      case 'generated':
        variant = 'primary';
        break;
      default:
        variant = 'secondary';
    }
    
    return <Badge bg={variant} className="status-badge">{status}</Badge>;
  };

  return (
    <div className="dso-invoice-details-page">
      <div className="page-header">
        <div className="left-section">
          <Link to="/dso/agent-invoices" className="back-link">
            <FaArrowLeft /> Back to Invoices
          </Link>
          <h2>Invoice Details</h2>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <Spinner animation="border" role="status" variant="primary" />
          <span>Loading invoice details...</span>
        </div>
      ) : error ? (
        <Alert variant="danger">
          <FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      ) : invoice ? (
        <>
          {/* Action Buttons */}
          <div className="action-buttons-container">
            <div className="approval-section">
              <div className="status-display">
                <span className="status-label me-2">Approval Status:</span>
                {renderStatusBadge(invoice.approval_status_str)}
              </div>
              <div className="status-display ms-3">
                <span className="status-label me-2">Payment Status:</span>
                {renderStatusBadge(invoice.payment_status_str)}
              </div>
            </div>
            
            <div className="button-group">
            {invoice.invoice_payout_type == 1 ?
              <>
              {invoice.invoice_status === 0 && (
                <Button
                  variant="success"
                  onClick={() => handleUpdateStatus(1, 0)} // Approve: approval=1, payment=0
                  disabled={actionLoading}
                  size="sm"
                  className="me-2"
                >
                  <FaCheckCircle/> {actionLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Approve'}
                </Button>
              )}

              {/* Reject Button: Show if approval is Pending (0) */}
              {invoice.invoice_status === 0 && (
                <Button
                  variant="danger"
                  onClick={() => handleUpdateStatus(2, 2)} // Reject: approval=2, payment=2
                  disabled={actionLoading}
                  size="sm"
                  className="me-2"
                >
                  <FaFrown/> {actionLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Reject'}
                </Button>
              )}

              {/* Disburse Button: Show if Approved (1) and Payment is Pending (0) */}
              {invoice.invoice_status === 1 && invoice.payment_status === 0 && (
                <Button
                  variant="info"
                  onClick={() => handleUpdateStatus(1, 1)} // Disburse: approval=1, payment=1
                  disabled={actionLoading}
                  size="sm"
                  className="me-2"
                >
                  <FaCaretSquareDown/> {actionLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Mark as Disbursed'}
                </Button>
              )}
              </>:<></>
            }

              {/* Existing Print Button (keep this) */}
              <Button 
                variant="primary" 
                onClick={handlePrintInvoice} 
                className="print-button"
                disabled={actionLoading} // Disable print button during action
              >
                <FaPrint className="me-2" />
                Print Invoice
              </Button>
            </div>
          </div>
          
          {/* Add these Alerts below the action-buttons-container div */}
          {actionError && (
            <Alert variant="danger" onClose={() => setActionError('')} dismissible className="mt-3 action-alert">
              {actionError}
            </Alert>
          )}
          {actionSuccess && (
            <Alert variant="success" onClose={() => setActionSuccess('')} dismissible className="mt-3 action-alert">
              {actionSuccess}
            </Alert>
          )}
          
          {/* Printable Invoice */}
          <div ref={printableRef} className="printable-invoice">
            <Container className="mt-4 mb-5">
              <Card className="invoice-card p-4 shadow-sm">
                <div className="invoice-title-section">
                  <div className="company-logo">
                    <img src="/static/images/Wishfin-logo.png" alt="Wishfin Logo" className="logo-image" />
                  </div>
                  <h2 className="text-center invoice-heading mb-0">INVOICE</h2>
                  <div className="invoice-meta">
                    <div className="invoice-number">
                      <span className="meta-label">Invoice #:</span>
                      <span className="meta-value">{invoice.invoice_no}</span>
                    </div>
                    <div className="invoice-date">
                      <span className="meta-label">Date:</span>
                      <span className="meta-value">{formatDate(invoice.created_at)}</span>
                    </div>
                    {invoice.payment_date && (
                      <div className="payment-date">
                        <span className="meta-label">Payment Date:</span>
                        <span className="meta-value">{formatDate(invoice.payment_date)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="invoice-parties">
                  <Row>
                  {invoice.invoice_payout_type == 1?<>
                      <Col md={6} className="party-section">
                        <div className="party-header">DSO Details:</div>
                        <div className="party-content">
                          <p><span className="party-label">Name:</span> {invoice.generator_name || 'DSO'}</p>
                        </div>
                      </Col>

                      <Col md={6} className="party-section">
                        <div className="party-header">RM Details:</div>
                        <div className="party-content">
                          <p><span className="party-label">Name:</span> {invoice.recipient_name || 'RM'}</p>
                        </div>
                      </Col>
                    </>:<>
                      <Col md={6} className="party-section">
                        <div className="party-header">Agent Details:</div>
                        <div className="party-content">
                          <p><span className="party-label">Name:</span> {invoice.generator_name || 'Agent'}</p>
                        </div>
                      </Col>

                      <Col md={6} className="party-section">
                        <div className="party-header">DSO Details:</div>
                        <div className="party-content">
                          <p><span className="party-label">Name:</span> {invoice.recipient_name || 'DSO'}</p>
                        </div>
                      </Col>
                  </>}
                    
                  </Row>
                </div>

                <hr className="invoice-divider" />

                <Table bordered responsive className="invoice-items-table">
                  <thead className="table-header">
                    <tr>
                      <th>Product Name</th>
                      <th>Product Category</th>
                      <th className="text-center">Number of Leads</th>
                      <th className="text-end">Total Payout Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.cases ? (
                      invoice.cases.map((item, index) => (
                        <tr key={item.id}>
                          <td>{item.product_name}</td>
                          <td>{item.product_category}</td>
                          <td className="text-center">1</td>
                          <td className="text-end">{formatCurrency(item.payout_amount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>{invoice.product_name || 'Product'}</td>
                        <td>{invoice.product_category || 'N/A'}</td>
                        <td className="text-center">{invoice.case_count || 1}</td>
                        <td className="text-end">{formatCurrency(invoice.amount)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="invoice-summary">
                  <Row className="mb-2">
                    <Col className="text-end">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-value">{formatCurrency(invoice.amount)}</span>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="text-end">
                      <span className="summary-label">GST (18%):</span>
                      <span className="summary-value">{formatCurrency(invoice.amount * 0.18 || invoice.gst_amount)}</span>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="text-end">
                      <span className="summary-label grand-total">Grand Total:</span>
                      <span className="summary-value grand-total">{formatCurrency(invoice.amount * 1.18 || invoice.total_amount)}</span>
                    </Col>
                  </Row>
                </div>

                <hr className="invoice-divider" />

                <div className="bank-details">
                  <h5>Agent's Bank Details:</h5>
                  <div className="bank-details-grid">
                    <p><span className="bank-label">Account Name:</span> {invoice.agent_name || 'Agent'}</p>
                    <p><span className="bank-label">Bank Name:</span> {invoice.agent_bank_name || 'N/A'}</p>
                    <p><span className="bank-label">Account Number:</span> {invoice.agent_account_number || 'N/A'}</p>
                    <p><span className="bank-label">IFSC Code:</span> {invoice.agent_ifsc_code || 'N/A'}</p>
                    <p><span className="bank-label">Branch Name:</span> {invoice.agent_branch_name || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="invoice-footer">
                  <p className="thank-you">Thank you for your business!</p>
                  <p className="footer-note">This is a computer generated invoice, no signature required.</p>
                </div>
              </Card>
            </Container>
          </div>
        </>
      ) : (
        <Alert variant="warning">
          Invoice not found or you don't have permission to view it.
        </Alert>
      )}
      
      <style jsx>{`
        .dso-invoice-details-page {
          padding: 20px;
          background-color: #f8fafc;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        /* Page Header Styles */
        .page-header {
          margin-bottom: 20px;
        }
        
        .left-section {
          display: flex;
          flex-direction: column;
          max-width: 100%;
        }
        
        .back-link {
          color: #4b5563;
          text-decoration: none;
          margin-bottom: 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.9rem;
          transition: color 0.2s;
        }
        
        .back-link:hover {
          color: #1f2937;
        }
        
        .page-header h2 {
          margin: 0 0 12px 0;
          font-size: 1.4rem;
          font-weight: 600;
          color: #111827;
        }
        
        /* Status Display */
        .status-display {
          display: flex;
          align-items: center;
        }
        
        .status-label {
          font-weight: 500;
          color: #4b5563;
          font-size: 0.85rem;
        }
        
        /* Loading State */
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          gap: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          color: #6b7280;
        }
        
        /* Action Buttons Container */
        .action-buttons-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
        }
        
        .approval-section {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .button-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        @media (max-width: 768px) {
          .action-buttons-container {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .button-group {
            margin-top: 10px;
            align-self: flex-start;
          }
        }
        
        .print-button {
          background-color: #f3f4f6;
          border-color: #d1d5db;
          color: #111827;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .print-button:hover {
          background-color: #e5e7eb;
        }
        
        /* Printable Invoice Styles */
        .printable-invoice {
          background-color: #fff;
        }
        
        .invoice-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }
        
        /* Invoice Title Section */
        .invoice-title-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          position: relative;
        }
        
        .company-logo {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .logo-image {
          height: 40px;
          width: auto;
          object-fit: contain;
        }
        
        .invoice-heading {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          font-size: 1.8rem;
          font-weight: 700;
          color: #111827;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .invoice-meta {
          display: flex;
          flex-direction: column;
          gap: 4px;
          text-align: right;
        }
        
        .invoice-number, .invoice-date, .payment-date {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        
        .meta-label {
          color: #6b7280;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        .meta-value {
          font-weight: 600;
          color: #111827;
          font-size: 0.9rem;
        }
        
        /* Invoice Parties */
        .invoice-parties {
          margin-bottom: 20px;
        }
        
        .party-section {
          margin-bottom: 16px;
        }
        
        .party-header {
          font-size: 1.05rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
        }
        
        .party-content p {
          margin-bottom: 6px;
          color: #4b5563;
          font-size: 0.95rem;
        }
        
        .party-label {
          font-weight: 600;
          color: #374151;
        }
        
        /* Divider */
        .invoice-divider {
          border-top: 1px solid #e5e7eb;
          margin: 20px 0;
        }
        
        /* Invoice Items Table */
        .invoice-items-table {
          margin-bottom: 20px;
        }
        
        .table-header th {
          background-color: #f3f4f6;
          color: #374151;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        /* Invoice Summary */
        .invoice-summary {
          margin-left: auto;
          width: 350px;
          font-size: 0.95rem;
        }
        
        .summary-label {
          font-weight: 500;
          color: #4b5563;
          margin-right: 12px;
          display: inline-block;
        }
        
        .summary-value {
          font-weight: 500;
          color: #111827;
          display: inline-block;
          width: 100px;
          text-align: right;
        }
        
        .grand-total {
          font-weight: 700;
          font-size: 1rem;
          color: #111827;
        }
        
        /* Bank Details */
        .bank-details {
          background-color: #f9fafb;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .bank-details h5 {
          font-size: 1.05rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
        }
        
        .bank-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 12px;
        }
        
        .bank-details-grid p {
          margin-bottom: 6px;
          color: #4b5563;
          font-size: 0.95rem;
        }
        
        .bank-label {
          font-weight: 600;
          color: #374151;
          margin-right: 6px;
        }
        
        /* Invoice Footer */
        .invoice-footer {
          text-align: center;
          margin-top: 30px;
        }
        
        .thank-you {
          font-size: 1.1rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        
        .footer-note {
          font-size: 0.85rem;
          color: #6b7280;
          font-style: italic;
        }

        /* Print Styles */
        @media print {
          .page-header,
          .action-buttons-container,
          .action-alert {
            display: none !important;
          }
          
          .printable-invoice {
            padding: 0;
            margin: 0;
          }
          
          .invoice-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default InvoiceDetails;
